import React, { useState, useEffect } from "react";
import { Form, InputGroup } from "react-bootstrap";
import * as S from "../../styles";
import "./styles.css";

export interface IMultiObjectCheckBoxProps {
    id: string;
    label?: string;
    value?: { [feature: string]: string[] };
    options?: {
        [feature: string]: { id: string; label: string }[];
    };
    readOnly?: boolean;
    errorText?: string;
    direction?: "horizontal" | "vertical";
    required?: boolean;
    labelColor?: string;
    hasError?: boolean;
    onCompChange?(id: string, payload: any): void;
}

export const MultiObjectCheckBox = ({
    id,
    label,
    value,
    options,
    errorText,
    onCompChange,
    required = false,
    readOnly = false,
    labelColor,
    hasError = false,
    direction = "vertical",
}: IMultiObjectCheckBoxProps) => {
    const [selected, setSelected] = useState<{ [key: string]: string[] }>(
        value ?? {}
    );
    const [isInvalid, setInvalid] = useState(false);

    useEffect(() => {
        if (hasError) setInvalid(true);
    }, [hasError]);

    const handleToggle = (feature: string, attr: string) => {
        const curr = new Set(selected[feature] ?? []);
        if (curr.has(attr)) curr.delete(attr);
        else curr.add(attr);

        const updated = { ...selected, [feature]: Array.from(curr) };
        setSelected(updated);
        onCompChange?.(id, updated);
    };

    const handleParentToggle = (feature: string) => {
        const allAttrs = options?.[feature]?.map((opt) => opt.id) ?? [];
        const allSelected = selected[feature]?.length === allAttrs.length;

        const updated = {
            ...selected,
            [feature]: allSelected ? [] : [...allAttrs],
        };
        setSelected(updated);
        onCompChange?.(id, updated);
    };

    const handleSelectAllToggle = () => {
        const isAllSelected = Object.entries(options ?? {}).every(
            ([feature, attrs]) => selected[feature]?.length === attrs.length
        );

        const updated: { [key: string]: string[] } = {};
        Object.entries(options ?? {}).forEach(([feature, attrs]) => {
            updated[feature] = isAllSelected ? [] : attrs.map((a) => a.id);
        });
        setSelected(updated);
        onCompChange?.(id, updated);
    };

    const isSelectAllChecked =
        Object.entries(options ?? {}).length > 0 &&
        Object.entries(options ?? {}).every(
            ([feature, attrs]) => selected[feature]?.length === attrs.length
        );

    return (
        <Form.Group id={id} className="mb-4 formgroup">
            {label && (
                <S.FormLabel color={labelColor}>
                    {label}
                    {required && <sup style={{ color: "red" }}>*</sup>}
                </S.FormLabel>
            )}

            <Form.Check
                className="formcheck form-check-select mb-2"
                type="checkbox"
                label={<strong>Select All</strong>}
                style={{ margin: "0 0 0 2%" }}
                checked={isSelectAllChecked}
                onChange={handleSelectAllToggle}
                disabled={readOnly}
            />

            {Object.entries(options ?? {}).map(([feature, attrs]) => (
                <div key={feature}>
                    <hr />
                    <Form.Check
                        type="checkbox"
                        label={<strong>{feature}</strong>}
                        className="formcheck"
                        style={{ margin: "2% 0 0 2%" }}
                        checked={selected[feature]?.length > 0}
                        onChange={() => handleParentToggle(feature)}
                        disabled={readOnly}
                    />

                    {selected[feature]?.length > 0 && (
                        <div style={{ marginLeft: 20 }}>
                            {direction === "horizontal" ? (
                                <div
                                    style={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                    }}
                                >
                                    {attrs.map((attr) => (
                                        <Form.Check
                                            key={attr.id}
                                            className="formcheck form-check-select"
                                            type="checkbox"
                                            label={attr.label}
                                            value={attr.id}
                                            checked={selected[
                                                feature
                                            ]?.includes(attr.id)}
                                            onChange={() =>
                                                handleToggle(feature, attr.id)
                                            }
                                            // isInvalid={isInvalid}
                                            readOnly={readOnly}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <div className="row">
                                    {attrs.map((attr) => (
                                        <div className="col-12" key={attr.id}>
                                            <Form.Check
                                                className="formcheck form-check-select"
                                                type="checkbox"
                                                label={attr.label}
                                                value={attr.id}
                                                checked={selected[
                                                    feature
                                                ]?.includes(attr.id)}
                                                onChange={() =>
                                                    handleToggle(
                                                        feature,
                                                        attr.id
                                                    )
                                                }
                                                // isInvalid={isInvalid}
                                                readOnly={readOnly}
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            ))}

            {hasError &&
                errorText &&
                (direction === "horizontal" ? (
                    <S.FeedbackOtherLabel type="tooltip">
                        {errorText}
                    </S.FeedbackOtherLabel>
                ) : (
                    <InputGroup>
                        <S.FeedbackOtherLabel type="tooltip">
                            {errorText}
                        </S.FeedbackOtherLabel>
                    </InputGroup>
                ))}
        </Form.Group>
    );
};

MultiObjectCheckBox.defaultProps = {
    direction: "vertical",
    required: false,
    hasError: false,
    readOnly: false,
    options: {},
};
